// Verbindung zur SRS Bridge auf dem Studio-Rechner (ws://127.0.0.1:47800).
// Die Bridge spricht mit Core Audio / ASIO (RME Digiface Dante, Dante Virtual Soundcard),
// nimmt lokal verlustfrei auf, hostet die eigenen Plugins und lädt Takes hoch.
import { BRIDGE_PORT, BRIDGE_PROTOCOL_VERSION, isFromBridge, parseBin, type FromBridge, type ToBridge, type DeviceInfo, type DeviceState, type PluginInfo, type BinHeader } from '@srs/shared';
import { useStudio } from '../state/store.ts';

type Handler = (m: FromBridge) => void;
type BinHandler = (m: BinHeader & { data: ArrayBuffer }) => void;
type DistOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;
export type BridgeRequest = DistOmit<ToBridge, 'id'> & { id?: string };

export interface BridgeStatus {
  connected: boolean; paired: boolean; version?: string; os?: string;
  devices: DeviceInfo[]; device?: DeviceState; plugins: PluginInfo[];
  /** RMS und Spitzenwert je Kanal (0..1, Reihenfolge wie abonniert: alle Eingänge des Geräts) */
  meters: number[]; peaks: number[]; latencyMs?: number; error?: string;
  /** Latenzmessung läuft */
  measuring?: boolean;
}

class BridgeClient {
  private ws: WebSocket | null = null;
  private handlers = new Set<Handler>();
  private binHandlers = new Set<BinHandler>();
  private retry = 0;
  private wanted = false;
  status: BridgeStatus = { connected: false, paired: false, devices: [], plugins: [], meters: [], peaks: [] };
  private statusListeners = new Set<(s: BridgeStatus) => void>();

  url = (import.meta.env.VITE_BRIDGE_URL as string | undefined) ?? `ws://127.0.0.1:${BRIDGE_PORT}`;

  connect(pairingCode?: string) {
    this.wanted = true;
    if (this.ws && this.ws.readyState <= 1) {
      // schon verbunden, aber noch nicht gekoppelt: Code nachreichen
      if (pairingCode && this.ws.readyState === 1) this.send({ type: 'hello', app: 'srs-studio', protocol: BRIDGE_PROTOCOL_VERSION, pairingCode });
      return;
    }
    let opened = false;
    try { this.ws = new WebSocket(this.url); } catch { this.scheduleRetry(); return; }
    this.ws.binaryType = 'arraybuffer';
    this.ws.onopen = () => {
      opened = true;
      this.retry = 0;
      this.patch({ error: undefined });
      this.send({ type: 'hello', app: 'srs-studio', protocol: BRIDGE_PROTOCOL_VERSION, pairingCode });
    };
    this.ws.onmessage = (e) => {
      if (e.data instanceof ArrayBuffer) {
        const b = parseBin(e.data);
        if (b) this.binHandlers.forEach((h) => h(b));
        return;
      }
      let m: unknown;
      try { m = JSON.parse(String(e.data)); } catch { return; }
      if (!isFromBridge(m)) return;
      this.reduce(m);
      this.handlers.forEach((h) => h(m as FromBridge));
    };
    this.ws.onclose = () => {
      this.patch({ connected: false, meters: [], peaks: [], measuring: false });
      useStudio.getState().setOnline({ bridge: false });
      if (!opened) void this.explainFailure();
      if (this.wanted) this.scheduleRetry();
    };
    this.ws.onerror = () => this.ws?.close();
  }

  /** Warum keine Verbindung? Chrome/Edge fragen seit 2025/2026 nach „Zugriff auf Apps auf diesem Gerät“ (Local Network Access). */
  private async explainFailure() {
    let state: string | undefined;
    try { state = (await navigator.permissions.query({ name: 'loopback-network' as PermissionName })).state; } catch { /* Browser kennt die Abfrage nicht */ }
    const safari = /^((?!chrome|android|crios|edg).)*safari/i.test(navigator.userAgent);
    this.patch({
      error: state === 'denied'
        ? 'Der Browser blockiert die Verbindung zur Bridge. Schloss-Symbol links in der Adresszeile → Website-Einstellungen → „Apps auf diesem Gerät“ bzw. „Lokales Netzwerk“ auf Zulassen, dann neu laden.'
        : safari
          ? 'Keine Verbindung zur Bridge. Safari blockiert Verbindungen zu Apps auf dem eigenen Rechner teilweise. Bitte Chrome oder Edge verwenden.'
          : 'Keine Verbindung zur Bridge. Läuft die SRS Studio Bridge (Symbol in der Menüleiste bzw. im Infobereich)? Fragt der Browser nach Zugriff auf „Apps auf diesem Gerät“, bitte zulassen.',
    });
  }

  /** Latenz per Loopback messen (Ausgang → Eingang) */
  measureLatency(outputChannel = 0, inputChannel = 0) {
    this.patch({ measuring: true, error: undefined });
    this.send({ type: 'measureLatency', outputChannel, inputChannel });
    setTimeout(() => { if (this.status.measuring) this.patch({ measuring: false, error: 'Latenzmessung: keine Antwort der Bridge.' }); }, 15000);
  }
  clearError() { this.patch({ error: undefined }); }

  disconnect() { this.wanted = false; this.ws?.close(); }

  private scheduleRetry() { const d = Math.min(10000, 500 * 2 ** this.retry++); setTimeout(() => this.wanted && this.connect(), d); }

  private reduce(m: FromBridge) {
    switch (m.type) {
      case 'helloAck':
        this.patch({ connected: true, paired: m.paired, version: m.bridgeVersion, os: m.os });
        useStudio.getState().setOnline({ bridge: true });
        if (m.protocol !== BRIDGE_PROTOCOL_VERSION) this.patch({ error: `Bridge spricht Protokoll ${m.protocol}, erwartet ${BRIDGE_PROTOCOL_VERSION}. Bitte Bridge aktualisieren.` });
        this.send({ type: 'listDevices' });
        this.send({ type: 'listPlugins' });
        break;
      case 'devices': this.patch({ devices: m.devices }); break;
      case 'deviceState': {
        // anderes Gerät: alte Pegel verwerfen
        if (m.state.deviceId !== this.status.device?.deviceId) this.patch({ meters: [], peaks: [] });
        // Gespeicherten Roundtrip übernehmen, damit er auch nach Neuladen sichtbar ist
        const rt = m.state.measuredRoundTripSamples;
        this.patch({ device: m.state, ...(rt ? { latencyMs: (rt / m.state.sampleRate) * 1000 } : {}) });
        break;
      }
      case 'plugins': this.patch({ plugins: m.plugins }); break;
      case 'meters': this.patch({ meters: m.levels, peaks: m.peak ?? m.levels }); break;
      case 'latency': this.patch({ latencyMs: m.roundTripMs, measuring: false, error: undefined }); break;
      case 'error': this.patch({ error: m.message, ...(m.code === 'latency_error' || m.code === 'busy' ? { measuring: false } : {}) }); break;
    }
  }

  private patch(p: Partial<BridgeStatus>) { this.status = { ...this.status, ...p }; this.statusListeners.forEach((l) => l(this.status)); }

  /** Sendet eine Anfrage; die id wird ergänzt und zurückgegeben (Antworten tragen dieselbe id) */
  send(m: BridgeRequest): string {
    const id = m.id ?? crypto.randomUUID();
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ ...m, id }));
    return id;
  }

  on(h: Handler) { this.handlers.add(h); return () => this.handlers.delete(h); }
  onBinary(h: BinHandler) { this.binHandlers.add(h); return () => this.binHandlers.delete(h); }

  /** Binärblock senden; wartet, solange der Sendepuffer voll ist (große Audio-Eingänge) */
  async sendBinary(buf: ArrayBuffer) {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) throw new Error('Keine Verbindung zur Bridge');
    while (ws.bufferedAmount > 8 * 1024 * 1024) {
      await new Promise((r) => setTimeout(r, 15));
      if (ws.readyState !== WebSocket.OPEN) throw new Error('Verbindung zur Bridge getrennt');
    }
    ws.send(buf);
  }
  /** Bridge kann eigene Effekt-Plugins rechnen (ab 0.3) */
  get effects() { const v = (this.status.version ?? '0').split('.').map(Number); return this.status.connected && this.status.paired && (v[0] > 0 || v[1] >= 3); }
  onStatus(l: (s: BridgeStatus) => void) { this.statusListeners.add(l); l(this.status); return () => this.statusListeners.delete(l); }

  /** Wartet auf eine bestimmte Antwort der Bridge */
  waitFor<T extends FromBridge['type']>(type: T, pred: (m: Extract<FromBridge, { type: T }>) => boolean = () => true, timeoutMs = 30000, requestId?: string) {
    return new Promise<Extract<FromBridge, { type: T }>>((resolve, reject) => {
      const t = setTimeout(() => { off(); reject(new Error(this.status.connected ? `keine Antwort der Bridge (${type})` : 'Verbindung zur Bridge getrennt')); }, timeoutMs);
      const off = this.on((m) => {
        if (m.type === type && pred(m as Extract<FromBridge, { type: T }>)) { clearTimeout(t); off(); resolve(m as Extract<FromBridge, { type: T }>); }
        // Fehlermeldung der Bridge zu genau dieser Anfrage: sofort abbrechen statt auf den Zeitablauf zu warten
        else if (m.type === 'error' && requestId && m.id === requestId) { clearTimeout(t); off(); reject(new Error(m.message)); }
      });
    });
  }
}

export const bridge = new BridgeClient();
